import styled from '@emotion/styled';
import { SocialLoginOptions } from '@eventbrite/auth-lib';
import { track } from '@eventbrite/datalayer-library';
import { gettext } from '@eventbrite/i18n';
import { Button, Typography } from '@eventbrite/marmalade';
import { useMutation } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useWindowSize } from 'react-use';
import { useAppContext } from '../../../AppContext';
import {
    AlertProps,
    FormHeader,
    IAuthProps,
    MOBILE_BREAKPOINT,
    People,
    Section,
    Whoops,
} from '../../../shared';
import {
    HEAP_EVENT_NAME_SIGNUP_SUCCESSFUL,
    LOGIN_METHOD_EMAIL,
    SIGNUP_TYPE_LOGGED_OUT,
    SIGN_UP_SUCCESS,
    SIGN_UP_TYPE_LOGGED_OUT_EVENT_CREATE,
    trackSocialSignUp,
} from '../../../shared/analytics';
import { LoadingSpinner } from '../../../shared/LoadingSpinner';
import { createAccount } from '../api';
import { getOrganizations } from '../api/getOrganizations';
import { handleAsyncActionFailure, useEventCreationProcess } from '../hooks';
import { CreateAccountFormValues } from '../types';
import { getSocialSignupConfiguration } from '../utils/getSocialSignupConfiguration';
import { AuxilliaryActionBar } from './AuxilliaryActionBar';
import { FormFields } from './FormFields';
import { Illustration } from './Illustration';
import { SignUpTitle } from './SignUpTitle';

export enum CreateAccountFormTestId {
    Container = 'CreateAccountAppContainer',
    ContinueButton = 'CreateAccountAccountCreatedContinueButton',
}

export const CreateAccountForm = ({
    handleModalClose,
    setIsSuccess,
    authProps,
}: {
    handleModalClose?: () => void;
    setIsSuccess?: (value: boolean) => void;
    authProps?: IAuthProps;
}) => {
    const [alertMessage, setAlertMessage] = useState<AlertProps | null>(null);
    const [organizationName, setOrganizationName] =
        useState<string>('My Organization');
    const [showContinueButton, setShowContinueButton] = useState(false);

    const { width } = useWindowSize();
    const {
        user,
        didCreateEvent,
        didCreateAccount,
        didRetry,
        eventCreationFailed,
        errorSimulation,
        isPerformingAsyncAction,
        retryCount,
        wasRetrySuccessful,
        shouldShowFullSignUpForm,
        isSocialSignUpLoading,
        updateContext,
    } = useAppContext();

    const { shouldErrorOnCreateAccount } = errorSimulation;

    const {
        createOrganizationAndEventMutation,
        isCreatingOrganizationAndOrEvent,
        retryEventCreationProcess,
        saveOnboardingResponsesMutation,
        onCreateOrganizationSuccess,
    } = useEventCreationProcess();

    const isMobile = width < MOBILE_BREAKPOINT;

    const methods = useForm<CreateAccountFormValues>({
        mode: 'onSubmit',
        reValidateMode: 'onBlur',
        defaultValues: {
            firstName: user?.firstName || '',
            lastName: user?.lastName || '',
            email: user?.email || '',
            password: '',
            confirmEmail: '',
            acceptedTerms: false,
        },
    });

    const createAccountMutation = useMutation({
        mutationFn: (accountDetails: CreateAccountFormValues) =>
            createAccount({
                accountDetails,
                options: {
                    testingFlags: {
                        shouldError: shouldErrorOnCreateAccount,
                    },
                },
            }),
        onSuccess: async (data: { id: string }) => {
            setIsSuccess && setIsSuccess(true);
            setShowContinueButton(true);
            const { id: publicId } = data;

            // Generic platform-wide signup event.
            track({
                eventName: HEAP_EVENT_NAME_SIGNUP_SUCCESSFUL,
                eventData: {
                    signupType: SIGNUP_TYPE_LOGGED_OUT,
                    signupMethod: LOGIN_METHOD_EMAIL,
                },
            });

            // Logged-out specific signup event.
            track({
                eventName: SIGN_UP_SUCCESS,
                eventData: {
                    signupType: SIGN_UP_TYPE_LOGGED_OUT_EVENT_CREATE,
                    signupMethod: LOGIN_METHOD_EMAIL,
                },
            });

            // save the public id in the context for use for retrying the organization creation if needed
            updateContext({
                didCreateAccount: true,
                didCreateAccountRequestFail: false,
                _publicId: publicId,
            });

            // using sessionStorage to store the publicId because the function that saves the onboarding responses
            // doesn't get the publicId from the context in time. the id is removed from storage after the responses are saved.
            sessionStorage.setItem('publicId', publicId);

            // check if the user has an organization
            const userOrganizations = await getOrganizations({ publicId });

            const organization = userOrganizations?.organizations?.[0];

            if (organization) {
                await onCreateOrganizationSuccess(organization);
            } else {
                createOrganizationAndEventMutation.mutate({
                    publicId,
                    name: organizationName,
                });
            }
        },
        onError: (error: any) => {
            updateContext({ isSocialSignUpLoading: false });
            handleAsyncActionFailure({
                error,
                action: 'create account',
                errorSimulation,
                updateContext,
            });
        },
    });

    const containerHeight = isMobile ? '100vh' : '100%';

    const submitForm = async (values: CreateAccountFormValues) => {
        if (!shouldShowFullSignUpForm) {
            updateContext({ shouldShowFullSignUpForm: true });
        } else {
            const { firstName, lastName } = values;
            setOrganizationName(`${firstName} ${lastName}`);
            updateContext({ isPerformingAsyncAction: true });
            createAccountMutation.mutate(values);
        }
    };

    const handleSocialSignUp = async (publicId: string) => {
        try {
            const userOrganizations = await getOrganizations({ publicId });
            const organizationId = userOrganizations?.organizations?.[0]?.id;

            if (!organizationId) {
                setOrganizationName('My Organization');
            }
            setShowContinueButton(true);

            updateContext({
                didCreateAccount: true,
                didCreateAccountRequestFail: false,
                _publicId: publicId,
                hasOrganization: organizationId ? true : false,
                _activeOrganizationId: organizationId,
            });

            sessionStorage.setItem('publicId', publicId);
            sessionStorage.setItem('activeOrganizationId', organizationId);

            if (organizationId) {
                saveOnboardingResponsesMutation.mutate({
                    organizationId,
                    publicId,
                });
            } else {
                createOrganizationAndEventMutation.mutate(
                    {
                        publicId,
                        name: organizationName,
                    },
                    {
                        onSettled: () =>
                            updateContext({ isSocialSignUpLoading: false }),
                    },
                );
            }
        } catch (e) {
            setAlertMessage({
                title: gettext('Something went wrong').toString(),
                description: (
                    <Typography
                        as="p"
                        color="neutral-800"
                        variant="body-md"
                        className="checkbox-label-text"
                    >
                        {gettext('Please try again.').toString()}
                    </Typography>
                ),
                variant: 'warning',
            });
            updateContext({ isSocialSignUpLoading: false });
        }
    };

    useEffect(() => {
        document.documentElement.style.setProperty(
            '--dynamic-height',
            containerHeight,
        );
    }, [containerHeight]);

    useEffect(() => {
        if (didCreateEvent) {
            track({
                eventName: 'LoggedOutCreatorSignupSuccessModal',
                eventData: {},
            });
        }
    }, [didCreateEvent]);

    const isSubmitting =
        createAccountMutation.isLoading || isCreatingOrganizationAndOrEvent;

    useEffect(() => {
        updateContext({
            isSubmittingCreateAccountForm: isSubmitting,
            isLoadingInInnerComponent: isSubmitting,
        });
    }, [isSubmitting, updateContext]);

    const retryButtonCTAText = didRetry
        ? gettext('Try again').toString()
        : gettext('Continue').toString();

    const socialConfig = getSocialSignupConfiguration({
        request: authProps?.request,
        gaSettings: authProps?.gaSettings,
        captcha: {
            recaptcha2PublicKey: authProps?.recaptcha2PublicKey,
            recaptcha3PublicKey: authProps?.recaptcha3PublicKey,
            disableRecaptcha3: authProps?.featureFlags?.disableRecaptcha3,
        },
        socialSSOSettings: authProps?.socialSSOSettings,
        fp: authProps?.fp,
        featureFlags: authProps?.featureFlags,
        onAttempt: () => {
            updateContext({ isSocialSignUpLoading: true });
        },
        onSuccess: async (res) => {
            const publicId = res?.publicId;
            const socialProvider = res?.socialProvider;
            trackSocialSignUp(socialProvider);
            handleSocialSignUp(publicId);
        },
        onError: (error) => {
            updateContext({
                enteredEmailExists: true,
                isSocialSignUpLoading: false,
            });
            setAlertMessage({
                title: error?.title,
                description: (
                    // @ts-expect-error Typography isn't used to dangerouslySetInnerHTML.
                    <Typography
                        as="p"
                        color="neutral-800"
                        variant="body-md"
                        className="checkbox-label-text"
                        dangerouslySetInnerHTML={{
                            __html: error?.message,
                        }}
                    />
                ),
                variant: 'warning',
            });
        },
    });

    const shouldShowLoadingSpinnerOverlay = () => {
        if (
            !shouldShowFullSignUpForm &&
            !eventCreationFailed &&
            (isSubmitting || isSocialSignUpLoading)
        ) {
            return true;
        } else {
            return false;
        }
    };

    const shouldShowInitialFormState =
        (!didCreateAccount || isSubmitting) && !eventCreationFailed;

    const shouldShowSocialSignUpButtons =
        !shouldShowFullSignUpForm && shouldShowInitialFormState;

    return (
        <FormProvider {...methods}>
            <form
                name="createAccountForm"
                onSubmit={methods.handleSubmit(async (data) => {
                    await submitForm(data);
                })}
                data-testid={CreateAccountFormTestId.Container}
                noValidate
            >
                <Parent height={containerHeight}>
                    <Content>
                        {shouldShowLoadingSpinnerOverlay() && (
                            <LoadingSpinner />
                        )}
                        {shouldShowInitialFormState && (
                            <HeaderContainer>
                                <FormHeader />
                                <Section spacing={0}>
                                    <SignUpTitle />
                                </Section>
                            </HeaderContainer>
                        )}

                        {shouldShowSocialSignUpButtons && (
                            <>
                                <SocialLoginContainer>
                                    <SocialLoginOptions
                                        configuration={socialConfig}
                                    />
                                </SocialLoginContainer>
                                <DividerContainer>
                                    <OrDivider>
                                        <OrDividerText>
                                            {gettext('or')}
                                        </OrDividerText>
                                    </OrDivider>
                                </DividerContainer>
                            </>
                        )}
                        {shouldShowInitialFormState && (
                            <>
                                <FormFields
                                    alertMessage={alertMessage}
                                    setAlertMessage={setAlertMessage}
                                    isMobile={isMobile}
                                    hasSubmissionError={
                                        createAccountMutation.isError
                                    }
                                    isSubmitting={isSubmitting}
                                />
                            </>
                        )}

                        {!didRetry && didCreateEvent && (
                            <Illustration
                                svg={<People />}
                                title={gettext(
                                    'Nice, your account was created! 🎉',
                                )}
                                body={gettext(
                                    "Your event was saved as a draft and you can continue editing it. We're glad you're here!",
                                )}
                            />
                        )}

                        {wasRetrySuccessful && (
                            <Illustration
                                svg={<People />}
                                title={gettext(
                                    'Nice, your event was saved successfully! 🎉',
                                )}
                                body={gettext(
                                    "Your event was saved as a draft and you can continue editing it. We're glad you're here!",
                                )}
                            />
                        )}

                        {eventCreationFailed ? (
                            !didRetry ||
                            (retryCount == 1 && isPerformingAsyncAction) ? (
                                <Illustration
                                    svg={<People />}
                                    title={gettext(
                                        'Nice, your account was created! 🎉',
                                    )}
                                    body={gettext(
                                        "We're glad you're here! Continue editing your event, then publish when you're ready.",
                                    )}
                                />
                            ) : (
                                <Illustration
                                    svg={<Whoops />}
                                    title={gettext("Let's try one more time")}
                                    body={gettext(
                                        "We need to try and retrieve your event details again. Don't worry, none of your event info was lost.",
                                    )}
                                />
                            )
                        ) : null}
                    </Content>

                    {eventCreationFailed && (
                        <AuxilliaryActionBar
                            primaryButtonText={retryButtonCTAText}
                            onClick={retryEventCreationProcess}
                            isSubmitting={isSubmitting}
                        />
                    )}

                    {showContinueButton &&
                        !isSubmitting &&
                        !eventCreationFailed && (
                            <ContinueButtonContainer>
                                <Button
                                    variant="primary"
                                    data-heap-id="continue-account-creation-logged-out-auto-create-event"
                                    data-testid={
                                        CreateAccountFormTestId.ContinueButton
                                    }
                                    onClick={() =>
                                        handleModalClose && handleModalClose()
                                    }
                                >
                                    {gettext('Continue')}
                                </Button>
                            </ContinueButtonContainer>
                        )}
                </Parent>
            </form>
        </FormProvider>
    );
};

const Content = styled.div`
    padding: 65px;
    display: flex;
    gap: 32px;
    flex-direction: column;
`;

const HeaderContainer = styled.div`
    display: flex;
    gap: 24px;
    flex-direction: column;
`;

const Parent = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    height: ${({ height }: { height: string }) => height || 'auto'};
`;

const ContinueButtonContainer = styled.div`
    border-top: 1px solid #eeedf2;
    background: transparent;
    display: flex;
    align-items: end;
    justify-content: flex-end;
    padding: 16px 24px;
`;

const SocialLoginContainer = styled.div`
    svg {
        overflow: visible;
    }
    font-size: 14px;
`;

const DividerContainer = styled.div``;

const OrDivider = styled.div`
    position: relative;
    align-items: center;
    display: flex;
    justify-content: center;
    &:before {
        content: '';
        height: 1px;
        position: absolute;
        left: 0;
        right: 0;
        background-color: rgba(221, 218, 227, 1);
    }
`;

const OrDividerText = styled.div`
    color: #716b7a;
    border: 1px solid rgba(221, 218, 227, 1);
    border-radius: 100px;
    background: white;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 2px 16px 4px 16px;
    box-sizing: border-box;
    font-size: 14px;
`;
